import {CREATEJS_UTILITIES_LOGGER, instanceOfClass} from "../../../../../index";
import {getLogger} from "@goodgamestudios/logger";
import { Transform } from "./Transform";
import Rectangle = createjs.Rectangle;
import DisplayObject = createjs.DisplayObject;
import Point = createjs.Point;
import MovieClip = createjs.MovieClip;
import Stage = createjs.Stage;
import {PixelSnapping} from "./PixelSnapping";
import Container = createjs.Container;
import Shape = createjs.Shape;

// Simulate as3 Transform
const logger = getLogger(CREATEJS_UTILITIES_LOGGER);

/*
 useDynamicTickBehaviour
 If set to true, the properties tickEnabled and tickChildren are false by default.
 Adding a onEnterFrame-EventListener activates tickEnabled/tickChildren.
 When adding this DisplayObject to a parent, the parent's values will be set as well.

 Enabling this will increase the speed of the game by up to 1 fps on a slow machine,
 but it is currently not recommended to use it.
 Known problem: children are never checked for it's tickbehaviour, therefore the value is not set correctly for many
 occurances regarding animated MovieClips in Assets.
 */
const useDynamicTickBehaviour: boolean = false;

Object.defineProperty(DisplayObject.prototype, "transform", {
    get: function(this: DisplayObject & { _transform: Transform | undefined }): Transform {
        if (!this._transform) {
            this._transform = new Transform(this);
        }
        return this._transform;
    },
    enumerable: true,
    configurable: true
});

DisplayObject.prototype.transformRect = function (rect: Rectangle,
                                                           targetSpace: DisplayObject): Rectangle {

    let x: number[] = [];
    let y: number[] = [];

    x[0] = rect.x;                  // 0 —————— 1
    y[0] = rect.y;                  // |        |
                                    // |        |
    x[1] = x[0] + rect.width;       // |        |
    y[1] = y[0];                    // 3 —————— 2

    x[2] = x[1];
    y[2] = y[0] + rect.height;

    x[3] = x[0];
    y[3] = y[2];

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    let point = new Point();

    for (let i = 0; i < 4; i++) {
        this.localToLocal(x[i], y[i], targetSpace, point);
        let px = point.x;
        let py = point.y;
        if (px < minX) { minX = px; }
        if (px > maxX) { maxX = px; }
        if (py < minY) { minY = py; }
        if (py > maxY) { maxY = py; }
    }

    return new Rectangle(minX, minY, maxX - minX, maxY - minY);
};

const originalGetBounds = DisplayObject.prototype.getBounds;
DisplayObject.prototype.getBounds = function(targetSpace?: DisplayObject): Rectangle {

    let bounds: Rectangle = originalGetBounds.apply(this);

    if (!bounds) {
        return null as any; // TODO: Workaround to appease the compiler; function needs to be correct to also allow `null` as a return value, which could happen even in the default implementation: https://www.createjs.com/docs/easeljs/files/easeljs_display_DisplayObject.js.html#l1145
    }

    if (targetSpace && targetSpace !== this) {
        return this.transformRect(bounds, targetSpace);
    } else {
        return bounds;
    }
};

type DisplayObjectWithFilters = DisplayObject & { _filters: any[] | undefined };

Object.defineProperty(DisplayObject.prototype, "filters", {
    get: function (this: DisplayObjectWithFilters) {
        return this._filters;
    },
    set: function (this: DisplayObjectWithFilters, value: any[]) {
        this._filters = value;
    },
    enumerable: true,
    configurable: true
});

DisplayObject.prototype.showFilter = function (this: DisplayObjectWithFilters): void {
    if (!this.parent) {
        return;
    }
    if (this._filters && this._filters.length > 0) {
        let bounds = this.getBounds();
        if (bounds && bounds.width && bounds.height ) {
            this.cacheCanvas ? this.updateCache() : this.cache(bounds.x, bounds.y, bounds.width, bounds.height, this.getDoCacheScale());
        }
    }
};

DisplayObject.prototype.removeFilter = function (): void {
    if (!this.parent) {
        return;
    }

    this.uncache();
};

type DisplayObjectWithRefToOnAddedClosure = DisplayObject & { _refToOnAddedClosure: any };

DisplayObject.prototype.useFilters = function(this: DisplayObjectWithRefToOnAddedClosure, filtersList: Array<createjs.Filter>, isAlwaysCached: boolean = false, scale:number = NaN): void {

    if (!this.filters && !filtersList) {
        return;
    }
    if (this.filters && filtersList) {
        // change from [] to []
        if (this.filters.length === 0 && filtersList.length === 0) {
            return;
        }
        if (this.filters.length === filtersList.length) {
            let allFiltersIdentical = true, i = 0;
            for ( i = 0; i < this.filters.length; i++) {
                allFiltersIdentical = allFiltersIdentical && this.filters[i].equals(filtersList[i]);
                if (!allFiltersIdentical) { break; }
            }
            if (allFiltersIdentical && this.cacheCanvas && this.cacheCanvas.width) { return; }
        }
    }
    this.filters = filtersList;

    const isCached = this.cacheCanvas != null;
    if (filtersList && filtersList.length > 0) {
        const bounds = this.getBounds();
        if (bounds && bounds.width && bounds.height ) {

            if(isCached) {
                this.updateCache();
            }
            else {
                if(isNaN(scale)) {
                    scale = this.getDoCacheScale();
                }
                this.cache(Math.floor(bounds.x), Math.floor(bounds.y), Math.ceil(bounds.width), Math.ceil(bounds.height), scale);
            }

            if (!this._refToOnAddedClosure) {
                let childChangeCallback = () => {
                    if (!this.filters || !this.filters.length) {
                        this.removeEventListener(createjs.Event.ADDED, childChangeCallback);
                        this.uncache();
                        this._refToOnAddedClosure = null;
                        return;
                    }
                    if (this.cacheCanvas) {
                        this.updateCache();
                    } else {
                        // TODO this might cause error in some cases becuase of bounds change, but that's rare, for performance we use old bounds
                        // we can change to `doCache` here, but needs more performance testing, since getBounds might be heavy
                        if(isNaN(scale)) {
                            scale = this.getDoCacheScale();
                        }
                        this.cache(Math.floor(bounds.x), Math.floor(bounds.y), Math.ceil(bounds.width), Math.ceil(bounds.height), scale);
                    }
                };
                this._refToOnAddedClosure = this.addEventListener(createjs.Event.ADDED, childChangeCallback);
            }
        }
    } else {
        if (isAlwaysCached && isCached) {
            this.updateCache();
        } else {
            this.uncache();
        }

        if (this._refToOnAddedClosure) {
            this.off(createjs.Event.ADDED, this._refToOnAddedClosure);
            this._refToOnAddedClosure = null;
        }
    }
};

Object.defineProperty(createjs.Bitmap.prototype, "_refToOnAddedClosure", {
    value: "",
    enumerable: true,
    configurable: true,
    writable: true
});

createjs.DisplayObject.prototype.getDoCacheScale = function(): number {
    let scale:number = 1;

    switch( createjs.DisplayObject.doCacheScalingMode ) {
        // check the scaling of the stage and apply it
        case createjs.DisplayObject.DOCACHE_SCALING_MODE_BY_STAGE:
            var aStage : createjs.Stage = this.stage ? this.stage : window ? (window as any).gamestage : null;
            if( aStage ) {
                scale = Math.ceil(aStage.scaleX);
            }
            break;

        // check  the scaling of all parents and apply it
        case createjs.DisplayObject.DOCACHE_SCALING_MODE_BY_ALL_PARENTS:
            let parent: createjs.Container|null = this.parent && this.parent.scaleX != undefined ? this.parent as createjs.Container : null;
            while( parent != null ) {
                scale *= parent.scaleX;
                parent = parent.parent && parent.parent.scaleX != undefined ? parent.parent as createjs.Container : null;
            }
            scale = Math.ceil( scale );
            break;
    }

    return scale;
}


DisplayObject.prototype.updateCacheIfCached = function( scale: number = 1): void {
    if (this.cacheCanvas) {
        this.doCache( 0, scale);
    }
};


createjs.DisplayObject.DOCACHE_SCALING_MODE_DEFAULT = 0;
createjs.DisplayObject.DOCACHE_SCALING_MODE_BY_STAGE = 1;
createjs.DisplayObject.DOCACHE_SCALING_MODE_BY_ALL_PARENTS = 2;
createjs.DisplayObject.doCacheScalingMode = 0;
/**
 * if doCache is called with scaling 1, setting the doCacheMode can alter the behaviour
 *
 * @param {number} scalingMode
 * default = take the number that came as a parameter
 * by stage = override by stage scaling
 * by all parents = override by stage scaling + parent scales
 */
createjs.DisplayObject.setDoCacheScalingMode = function( scalingMode : number = createjs.DisplayObject.DOCACHE_SCALING_MODE_DEFAULT ): void {
    createjs.DisplayObject.doCacheScalingMode = scalingMode;
};

// use addedBorderSize to make the cache xPixel bigger than the actual image to get rid of cut-off images when calling the faster updateCache method
DisplayObject.prototype.doCache = function( addedBorderSize: number = 0, scale: number = NaN ): void {
    // uncache first, otherwise getBounds will return false data
    if ( this.cacheCanvas ) {
        this.uncache();
    }
    const bounds = this.getBounds();

    if(isNaN(scale)) {
        scale = this.getDoCacheScale();
    }

    if (bounds && bounds.width && bounds.height) {
        // apply addedBorderSize
        if ( addedBorderSize !== 0 ) {
            bounds.x -= addedBorderSize;
            bounds.y -= addedBorderSize;
            bounds.width += addedBorderSize * 2;
            bounds.height += addedBorderSize * 2;
        }
        this.cache(Math.floor(bounds.x), Math.floor(bounds.y), Math.ceil(bounds.width), Math.ceil(bounds.height), scale);
    }
};

DisplayObject.prototype.getRect = function(this: DisplayObject, targetSpace: DisplayObject): Rectangle {
    return this.getBounds(targetSpace)!; // `getRect` is probably wrongly typed and could also return `null`
};

const originalGlobalToLocal = DisplayObject.prototype.globalToLocal;
DisplayObject.prototype.globalToLocal = function(point?: Point | number, y?: number, pt?:
    Object | Point): Point {
    if (point instanceof Point) {
        return originalGlobalToLocal.apply(this, [(point as Point).x, (point as Point).y]);
    }
    return originalGlobalToLocal.apply(this, [point as number, y, pt]);
};

const originallocalToGlobal = DisplayObject.prototype.localToGlobal;
DisplayObject.prototype.localToGlobal = function(point?: Point | number, y?: number, pt?:
    Object | Point): Point {
    if (point instanceof Point) {
        return originallocalToGlobal.apply(this, [(point as Point).x, (point as Point).y]);
    }
    return originallocalToGlobal.apply(this, [point as number, y, pt]);
};

type DisplayObjectWithAdjustScale9Grid = DisplayObject & { adjustScale9Grid: Function };
type DisplayObjectWith__Scale9Grid = DisplayObject & { __scale9Grid: Rectangle, getScale9GridDiff: Function };

(DisplayObject.prototype as any).getScale9GridDiff = function(this: DisplayObjectWith__Scale9Grid) {
    let bounds = this.getBounds();
    let minScale9GridWidth = this.__scale9Grid.left + bounds.width - this.__scale9Grid.right;
    let minScale9GridHeight = this.__scale9Grid.top + bounds.height - this.__scale9Grid.bottom;
    let result: any = {};

    if (this.width < minScale9GridWidth) {
        result.left = bounds.left;
        result.right = bounds.right;
    }
    if (this.height < minScale9GridHeight) {
        result.top = bounds.top;
        result.bottom = bounds.bottom;
    }

    return result;
};

(DisplayObject.prototype as any).adjustScale9Grid = function (this: DisplayObjectWith__Scale9Grid) {
    if (!this.__scale9Grid) {
        this.__scale9Grid = this.scale9Grid.clone();
    }

    let s9gDiff = this.getScale9GridDiff();

    if (Object.keys(s9gDiff).length) {
        this.scale9Grid = Object.assign(this.scale9Grid, s9gDiff);
    } else {
        this.scale9Grid = this.__scale9Grid.clone();
    }
};

// Simulate as3 object.width, easeljs operates only on getBounds() and setBounds()
Object.defineProperty(DisplayObject.prototype, "width", {
    get: function(this: DisplayObject) {
        const objBounds = this.getBounds();
        return objBounds ? Math.abs(objBounds.width * this.scaleX) : 0;
    },
    set: function(this: DisplayObjectWithAdjustScale9Grid, value) {

        // in Flash width cannot be negative
        if (value < 0) { return; }

        const objBounds = this.getBounds();

        if (objBounds) {
            if (objBounds.width) {
                this.scaleX = value / objBounds.width;
            }
        } else {
            throw new Error("Trying to change width while bounds are not defined");
        }
    },
    enumerable: true,
    configurable: true
});

// Simulate as3 object.height, easeljs operates only on getBounds() and setBounds()
Object.defineProperty(DisplayObject.prototype, "height", {
    get: function(this: DisplayObject) {
        const objBounds = this.getBounds();
        return objBounds ? Math.abs(objBounds.height * this.scaleY) : 0;
    },
    set: function(this: DisplayObjectWithAdjustScale9Grid, value) {

        // in Flash height cannot be negative
        if (value < 0) { return; }

        const objBounds = this.getBounds();
        if (objBounds) {
            if (objBounds.height) {
                this.scaleY = value / objBounds.height;
            }
        } else {
            throw new Error("Trying to change height while bounds are not defined");
        }
    },
    enumerable: true,
    configurable: true
});

type DisplayObjectWithScaleX= DisplayObject & { _scaleX: number | undefined, adjustScale9Grid: Function };

Object.defineProperty(DisplayObject.prototype, "scaleX", {
    get: function(this: DisplayObjectWithScaleX) {
        return this._scaleX;
    },
    set: function(this: DisplayObjectWithScaleX, value) {
        this._scaleX = value;
        if (this.scale9Grid) {
            this.adjustScale9Grid();
        }
    },
    enumerable: true,
    configurable: true
});

type DisplayObjectWithScaleY= DisplayObject & { _scaleY: number | undefined, adjustScale9Grid: Function };

Object.defineProperty(DisplayObject.prototype, "scaleY", {
    get: function(this: DisplayObjectWithScaleY) {
        return this._scaleY;
    },
    set: function(this: DisplayObjectWithScaleY, value) {
        this._scaleY = value;
        if (this.scale9Grid) {
            this.adjustScale9Grid();
        }
    },
    enumerable: true,
    configurable: true
});

type DisplayObjectWithUsedAsMask= DisplayObject & { _usedAsMask: boolean | undefined };

Object.defineProperty(DisplayObject.prototype, "usedAsMask", {
    get: function(this: DisplayObjectWithUsedAsMask) {
        if ( this._usedAsMask === undefined ) {
            return false;
        }
        return this._usedAsMask;
    },
    set: function(this: DisplayObjectWithUsedAsMask, value) {
        this._usedAsMask = value;
    },
    enumerable: true,
    configurable: true
});

type DisplayObjectWithAnyMask= DisplayObject & { _mask: any }; // TODO: What would be the narrowest type here?

const drawShapeFromBounds = function(value: any) {
    let bounds = value.getBounds();
    let shape = new Shape();
    let radius = isNaN(value.useAsMaskCornerRadius) ? 7 : value.useAsMaskCornerRadius;
    shape.setBounds(bounds.x, bounds.y, value.width, value.height);
    shape.graphics.f("#000000").drawRoundRect(bounds.x + value.x, bounds.y + value.y, value.width, value.height, radius);
    shape.visible = false;
    shape.usedAsMask = true;
    shape.name = "fakeMask";

    return shape;
};

Object.defineProperty(DisplayObject.prototype, "mask", {
    get: function(this: DisplayObjectWithAnyMask) {
        return this._mask;
    },
    set: function(this: DisplayObjectWithAnyMask, value) {
        if (!value) {
            this._mask = value;
            return;
        }
        if ( value !== this._mask ) {
            value.visible = false;
            value._usedAsMask = true;

            if ( this._mask ) {
                this._mask._usedAsMask = false;
            }

            if ( value instanceof Shape) {
                this._mask = value;
            } else if (value.graphics && value.graphics.instructions.length > 0 ) {
                // match container with programatical graphic
                this._mask = value;
            } else if (value instanceof MovieClip) {
                let mcMask = value._createMaskShape();
                if (this === (value as any).parent) {
                    mcMask.x = this.x;
                    mcMask.y = this.y;
                }
                this._mask = mcMask;
            } else if (value.getBounds) {
                const fakeMask = drawShapeFromBounds(value);
                if (this === value.parent) {
                    fakeMask.x = this.x;
                    fakeMask.y = this.y;
                }
                this._mask = fakeMask;
            }
        }
    },
    enumerable: true,
    configurable: true
});

DisplayObject.prototype.removed = function() {
    this.dispatchEvent(new createjs.Event("removed", false, false));
    this.parent = null;
    this.childrenRemovedStage();
};

DisplayObject.prototype.childrenRemovedStage = function(this: DisplayObjectOrContainerWithStage) {
    if ((this as Container).children) {
        let kids = (this as Container).children;
        for (let i = 0, l = kids.length; i < l; i++) {
            if (kids[i] != null && kids[i].added) {
                kids[i].childrenRemovedStage();
            }
        }
    }
    if (this._stage) {
        this.dispatchEvent(new createjs.Event(createjs.Event.REMOVED_FROM_STAGE, false, false));
        this._stage = null;
    }
};

DisplayObject.prototype.added = function(this: DisplayObject | Container) {
    // TODO: HTML-2590 - the dispatching of the Event.ADDED must be fixed.
    this.dispatchEvent(new createjs.Event(createjs.Event.ADDED, true, true));
    // this.dispatchEvent(new createjs.Event(createjs.Event.ADDED, false, false));
    this.checkAddedStage();

    if ( useDynamicTickBehaviour ) {
        // applying TickChildren / tickEnabled property
        // if it has a listener, set tickchildren to true to itself and it's parents
        if (this.hasEventListener("tick") || this instanceof createjs.MovieClip) {
            this.tickEnabled = true;
            if (this instanceof Container) {
                this.tickChildren = true;
            }
            if (this.parent) {
                let parent: Container | null = this.parent;
                // if the parent is already ticking, look no further
                while (parent && !parent.tickEnabled && !parent.tickChildren) {
                    parent.tickEnabled = true;
                    parent.tickChildren = true;
                    parent = parent.parent;
                }
            }
        } else {
            // if nobody is listening for it, there is no need to tick
            this.tickEnabled = false;
            if (this instanceof Container) {
                this.tickChildren = false;
            }
        }
    }
    // ensure DO is not positioned on sub-pixels
    this.x = Math.round(this.x);
    this.y = Math.round(this.y);
};

const originaladdEventListener = DisplayObject.prototype.addEventListener;
DisplayObject.prototype.addEventListener = function(type: string, listener: Function, useCapture?: boolean) {
    const handler = originaladdEventListener.apply(this, [type, listener, useCapture]);

    if ( useDynamicTickBehaviour ) {
        // if listening on ENTER_FRAME / Tick, we have to enable this property, as it should be false otherwise
        if (type === "tick" && !this.tickEnabled) {
            this.tickEnabled = true;
            if (this instanceof Container) {
                this.tickChildren = true;
            }

            // we need to enable all parents as well
            if (this.parent) {
                let parent: Container | null = this.parent;

                // if the parent is ticking already, look no further
                while (parent && !parent.tickEnabled && !parent.tickChildren) {
                    parent.tickEnabled = true;
                    parent.tickChildren = true;
                    parent = parent.parent;
                }
            }
        }
    }
    return handler;
};

type DisplayObjectOrContainerWithStage = (DisplayObject | Container) &
            {
                _stage: createjs.Stage | null | undefined ,
                _listeners: null | {[name: string]: Array<EventListener>},
                _hasUncacheListener: boolean
            };


DisplayObject.prototype.checkAddedStage = function(this: DisplayObjectOrContainerWithStage) {
    let stage = this.stage;
    if (stage) {
        this._stage = stage;
        this.dispatchEvent(new createjs.Event(createjs.Event.ADDED_TO_STAGE, false, false));
    }
    if (!this._hasUncacheListener) {
        let f = () => {
            if (this.cacheCanvas) {
                this.uncache();
                if (this.filters && this.filters.length > 0) {
                    this.filters = [];
                }
            }
            this.removeEventListener(createjs.Event.REMOVED_FROM_STAGE, f);
            this._hasUncacheListener = false;
        };
        this.addEventListener(createjs.Event.REMOVED_FROM_STAGE, f);
        this._hasUncacheListener = true;
    }
    if ((this as Container).children) {
        let kids = (this as Container).children;
        for (let i = 0, l = kids.length; i < l; i++) {
            if (kids[i].added) {
                kids[i].checkAddedStage();
            }
        }
    }
};

DisplayObject.prototype.hitTestPoint = function(x: number, y: number, shapeFlag: boolean = false): boolean {
    // in AS3, the API indicates x and y are global, but for createjs, we need local
    let local = this.globalToLocal(new Point(x, y));
    return this.hitTest(local.x, local.y);
};

DisplayObject.prototype.hitTestObject = function(obj: DisplayObject): boolean {
    // DO NOT RENOVE IT: It is a proper way how to calculate the hitTestObject
    // must be fixed by HTML-1496
    // let thisBounds = this.getBounds();
    // let thisPadding = this.localToGlobal(thisBounds.x, thisBounds.y);
    let thisPadding = this.localToGlobal(0, 0);
    let thisBorder = new Rectangle(thisPadding.x, thisPadding.y, this.width, this.height);

    // DO NOT RENOVE IT: It is a proper way how to calculate the hitTestObject
    // must be fixed by HTML-1496
    // let objBounds = obj.getBounds();
    // let objPadding = obj.localToGlobal(objBounds.x, objBounds.y);
    let objPadding = obj.localToGlobal(0, 0);
    let objBorder = new Rectangle(objPadding.x, objPadding.y, obj.width, obj.height);

    return thisBorder.intersects(objBorder);
};

DisplayObject.prototype.getFilterBounds = function(this: (DisplayObject & { _getFilterBounds: () => Rectangle })): Rectangle {
    return this._getFilterBounds();
};

DisplayObject.prototype.globalToLocal3D = function(point: Point): any {
    return null;
};

type DisplayObjectWithBlendShader = DisplayObject & { _blendShader: string | undefined };

Object.defineProperty(DisplayObject.prototype, "blendShader", {
    get: function(this: DisplayObjectWithBlendShader): void {
        throw new Error("blendShader has only setter");
    },
    set: function(this: DisplayObjectWithBlendShader, value: string) {
        this._blendShader = value;
    },
    enumerable: true,
    configurable: true
});

Object.defineProperty(DisplayObject.prototype, "root", {
    get: function(): DisplayObject | null {
        return null;
    },
    set: function(value: DisplayObject) {
        throw new Error("root has only getter");
    },
    enumerable: true,
    configurable: true
});

Object.defineProperty(DisplayObject.prototype, "autoAlpha", {
    get: function(this: DisplayObject): number {
        return this.alpha;
    },
    set: function(this: DisplayObject, value: number) {
        this.alpha = value;
        this.visible = !!value;
    },
    enumerable: true,
    configurable: true
});

DisplayObject.prototype.countCachedCanvas = function(result = {total: 0, underCacheTotal: 0}, underCache = false) {
    if (this.cacheCanvas) {
        result.total++;
        if ((!this.filters || this.filters.length === 0) && underCache) {
            // cache with filters need to be preserved, so don't count them
            result.underCacheTotal++;
        }
    }
    if (this instanceof Container) {
        let parentIsCached = underCache || !!this.cacheCanvas;
        for (let i = 0; i < this.children.length; i++) {
            this.children[i].countCachedCanvas(result, parentIsCached);
        }
    }
    return result;
};

DisplayObject.prototype.clearCacheUnderCache = function(underCache = false) {
    let parentIsCached = underCache || !!this.cacheCanvas;
    if (underCache && (!this.filters || this.filters.length === 0) && this.cacheCanvas) {
        // cache with filters should be preserved
        this.uncache();
    }
    if (this instanceof Container) {
        for (let i = 0; i < this.children.length; i++) {
            let child = this.children[i];
            child.clearCacheUnderCache(parentIsCached);
        }
    }
};

let originalUncache = DisplayObject.prototype.uncache;
DisplayObject.prototype.uncache = function() {
    if (this.cacheCanvas) {
        this.cacheCanvas.width = this.cacheCanvas.height = 0;
    }
    originalUncache.call(this);
};

/**
 * Override DisplayObject internal apply filters function, we need to hack out own implementation of fast filters
 */
(DisplayObject.prototype as any)._applyFilters = function() {
    if (!this.filters || this.filters.length === 0 || !this.cacheCanvas) { return; }
    if (this instanceof createjs.TextField) { return; }
    let l = this.filters.length;
    let ctx = this.cacheCanvas.getContext("2d");
    let w = this.cacheCanvas.width;
    let h = this.cacheCanvas.height;

    ctx._filterOffsetX = this._filterOffsetX;
    ctx._filterOffsetY = this._filterOffsetY;
    ctx._cacheOffsetX = this._cacheOffsetX;
    ctx._cacheOffsetY = this._cacheOffsetY;

    // hack
    if (this.allowFastGlow !== false ) {
        ctx._doFastGlow = true;
        ctx._cacheScale = this._cacheScale;
    }
    for (let i = 0; i < l; i++) {
        this.filters[i].applyFilter(ctx, 0, 0, w, h);
    }
};

Object.defineProperty(DisplayObject.prototype, "showDebugBounds", {
    get: function(this: DisplayObject & {_debugBoundsRectangle: Shape | null}): boolean {
        return !!this._debugBoundsRectangle;
    },
    set: function(this: DisplayObject & {_debugBoundsRectangle: Shape | null}, value: boolean) {
        if (value && !this._debugBoundsRectangle) {
            if (!this.parent) {
                throw new Error("Can't show debug rectangle since parent is undefined");
            }
            let bounds = this.getBounds();
            if (!bounds || !bounds.width || !bounds.height) {
                throw new Error("Can't show debug rectangle since bounds is 0");
            } else {
                this._debugBoundsRectangle = new Shape();
                this._debugBoundsRectangle.name = (this.name || this.id) + "_debugBoundsRectangle";
                this._debugBoundsRectangle.graphics
                .beginStroke("#FF0000")
                .drawRect(0, 0, bounds.width, bounds.height)
                .mt(0, 0)
                .bf("#0000FF")
                .dc(0, 0, 5)
                .ef()
                .endStroke();
                this._debugBoundsRectangle.x = this.x;
                this._debugBoundsRectangle.y = this.y;
                this.parent.addChild(this._debugBoundsRectangle);
            }
        } else if (!value && this._debugBoundsRectangle) {
            (this.parent as Container).removeChild(this._debugBoundsRectangle);
            this._debugBoundsRectangle = null;
        }
    },
    enumerable: true,
    configurable: true
});

/**
 * Generates a Matrix2D object representing the combined transform of the display object and all of its
 * parent Containers up to the highest level ancestor (usually the {{#crossLink "Stage"}}{{/crossLink}}). This can
 * be used to transform positions between coordinate spaces, such as with {{#crossLink "DisplayObject/localToGlobal"}}{{/crossLink}}
 * and {{#crossLink "DisplayObject/globalToLocal"}}{{/crossLink}}.
 * @method getConcatenatedMatrix
 * @param {Matrix2D} [matrix] A {{#crossLink "Matrix2D"}}{{/crossLink}} object to populate with the calculated values.
 * If null, a new Matrix2D object is returned.
 * @return {Matrix2D} The combined matrix.
 **/
DisplayObject.prototype.getConcatenatedMatrix = function(matrix) {
    let o: DisplayObject = this as DisplayObject, mtx = this.getMatrix(matrix);
    if(this instanceof Stage) {
        mtx.scale(1/this.scaleX,1/this.scaleY);
    }
    while (o = o.parent as DisplayObject) {
        if(!(o instanceof Stage)) {
            mtx.prependMatrix(o.getMatrix((o as any)._props.matrix));
        }
    }
    return mtx;
};

DisplayObject.prototype.isInContainer = function(container: Container): boolean {
    let p = this.parent;
    while(p) {
        if(p === container) {
            return true;
        }
        p = p.parent;
    }
    return false;
}
